import { useDispatch, useSelector } from "react-redux"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { ServiceDebitContract, ServiceDebitStatus } from "src/common/models/serviceContractDebit"
import { PageCard } from "src/common/styles/styles"
import { Loader } from "src/components/Loader"
import { SearchableTable } from "src/components/SearchableTable"
import { GET_CONTRACT_DEBIT_BY_ID_API, GET_SERVICE_CONTRACT_DEBITS_API } from "src/features/ServiceContract/state/actionTypes"
import { RootState } from "src/state/reducer"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { ColumnsType } from "antd/es/table"
import { ServiceContract } from "src/common/models/contract"
import { useEffect, useState } from "react"
import { cleanCurrentContract, cleanCurrentContractDebit, getContractDebitByIdApi, getServiceContractByIdApi, getServiceContractDebitsApi } from "src/features/ServiceContract/state/action"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Select, Space, Tooltip, notification } from "antd"
import { SystemDescriptions } from "src/common/descriptions"
import { ProfileOutlined, CreditCardOutlined, DeleteOutlined } from '@ant-design/icons'
import { ContractConfirmation } from "src/features/ServiceContract/CreateServiceContract/ContractConfirmation"
import { CreatePayment } from "src/features/ServiceContract/CreateServiceContract/TicketSetction/CreatePayment"
import useUserPermission from "src/hooks/useUserPermissions"
import { USER_SCOPES } from "src/hooks/UserPermission"
import { createAuthTokenApi } from "src/features/Security/state/action"
import { AuthTokenValidator } from "src/components/AuthTokenValidator"
import { AuthTokenRequest } from "src/common/models/auth"
import { ContractDebitDelete } from "src/features/PayableContract/ContractDebitDelete"
import { CREATE_AUTH_TOKEN_API } from "src/features/Security/state/actionTypes"
import { getServiceCategoriesApi } from "src/features/Service/state/action"
import { selectFilterOption } from "src/common/util"
import { ServiceCategory } from "src/common/models/serviceCategory"

export type ReduxProps = {
    serviceContractDebits?: ServiceDebitContract[],
    isGettingContractDebits: boolean
    currentContract?: Partial<ServiceContract>
    isGettingContract: boolean
    isGettingContractDebit: boolean
    currentContractDebit?: ServiceDebitContract
    createAuthTokenSuccess: boolean
    isCreatingAuthToken: boolean
    categories?: ServiceCategory[]
}

export const ServiceContractDebits = () => {
    const dispatch = useDispatch()
    const { validateUserScopeForPage } = useUserPermission()

    const [selectedAction, setSelectedAction] = useState<string | undefined>()
    const [refId, setRefId] = useState<string | undefined>(undefined)
    const [tokenValidatorOpen, setTokenValidatorOpen] = useState<boolean>(false)
    const [serviceContractToDelete, setServiceContractToDelete] = useState<ServiceDebitContract | undefined>(undefined)
    const [selectedFilterElements, setSelectedFilterElements] = useState<string[]>([])
    const [elementsToDisplay, setElementsToDisplay] = useState<ServiceDebitContract[]>([])

    const deleteDescriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.DELTE_CONTRACT_DEBIT_RECORD

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        serviceContractDebits: state.serviceContract.serviceContractDebits || [],
        isGettingContractDebits: serviceLoading(state, [GET_SERVICE_CONTRACT_DEBITS_API]),
        currentContract: state.serviceContract.currentContract,
        isGettingContract: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
        currentContractDebit: state.serviceContract.currentContractDebit,
        isGettingContractDebit: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
        createAuthTokenSuccess: state.security.createAuthTokenSuccess,
        isCreatingAuthToken: serviceLoading(state, [CREATE_AUTH_TOKEN_API]),
        categories: state.service.serviceCategories?.sort((a, b) => a.name.localeCompare(b.name)) || [],
    }))

    useEffect(() => {
        if (selectedFilterElements.length === 0) {
            setElementsToDisplay(reduxProps.serviceContractDebits || [])
        } else {
            setElementsToDisplay((reduxProps.serviceContractDebits || []).filter(scd => {
                return selectedFilterElements.includes(scd.categoriesInDetailIds?.toString() || "")
            }))
        }
    }, [selectedFilterElements, reduxProps.serviceContractDebits])

    const initData = () => {
        dispatch(getServiceContractDebitsApi({
            status: ServiceDebitStatus.PENDING_PAYMENT,
        }))
        dispatch(getServiceCategoriesApi())
    }

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.SERVICE_CONTRACT_DEBIT_READ)
        initData()
    }, [])

    useEffect(() => {
        if (reduxProps.createAuthTokenSuccess && selectedAction === "DELETE_SCD") {
            setTokenValidatorOpen(true)
        }
    }, [reduxProps.createAuthTokenSuccess])

    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.SERVICE_CONTRACT_DEBITS
    const createDescriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContractDebit || reduxProps.isCreatingAuthToken}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const tblColumns: ColumnsType<ServiceContract> = [
        ...TABLE_COLUMNS,
        {
            title: "actions",
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceDebitContract) => {
                const handleViewClick = (contractId: number) => {
                    setSelectedAction("VIEW")
                    dispatch(getServiceContractByIdApi(contractId))
                }

                const handleCreatePaymentClick = (contractId: number) => {
                    setSelectedAction("CREATE_PAYMENT")
                    dispatch(getContractDebitByIdApi(contractId))
                }

                const handleDeleteRecord = (payableContractDebitId: number) => {
                    setSelectedAction("DELETE_SCD")
                    setRefId(payableContractDebitId.toString())
                    dispatch(createAuthTokenApi({
                        purpose: "DELETE_SCD",
                        refId: payableContractDebitId.toString(),
                    }))
                }

                return <Space size="middle">
                    <Tooltip title={descriptions.VIEW_CONTRACT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.serviceContractId!)}
                        />
                    </Tooltip>
                    <Tooltip title={descriptions.CREATE_PAYMENT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<CreditCardOutlined />}
                            onClick={() => handleCreatePaymentClick(record.id!)}
                        />
                    </Tooltip>
                    <Tooltip
                        title={commonDescriptions.ACTIONS.DELETE}
                    >
                        <Button type="primary" danger ghost shape="circle" size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteRecord(record.id!)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const closeModal = () => {
        dispatch(cleanCurrentContract())
        dispatch(cleanCurrentContractDebit())
        setSelectedAction(undefined)
    }

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const onValidateAuthTokenSuccess = (authTokenRequest: AuthTokenRequest) => {
        setTokenValidatorOpen(false)
        setSelectedAction("DELETE_SCD")
        const foundPayableContract = reduxProps.serviceContractDebits?.find(pcd => pcd.id === Number(authTokenRequest.refId))
        if (foundPayableContract) {
            setServiceContractToDelete(foundPayableContract)
        }
    }

    const handleFilterChange = (e) => {
        setSelectedFilterElements(e.map(String))
    }

    const renderFilterSection = () => {
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'end' }}>

                <Select
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Selecciona uno o varios servicios"
                    onChange={handleFilterChange}
                    options={reduxProps.categories?.map(service => ({
                        value: service.id?.toString()!,
                        label: service.name,
                    }))}
                    filterOption={selectFilterOption}
                />
            </div>
        )
    }

    return (
        <>
            <PageCard
                size="small"
                title={descriptions.TITLE}
            >
                <SearchableTable
                    showSearch
                    showLoader={reduxProps.isGettingContractDebits}
                    items={elementsToDisplay}
                    tableColumns={tblColumns as any}
                    searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                    summaryColumns={summaryColumns}
                >
                    {renderFilterSection()}
                </SearchableTable>
                {
                    reduxProps.currentContract && selectedAction === "VIEW" &&
                    <ContractConfirmation
                        open={!!reduxProps.currentContract}
                        onCancel={() => { closeModal() }}
                        onAccept={() => { closeModal() }}
                        okText={commonDescriptions.ACCEPT}
                        cancelText={commonDescriptions.CANCEL}
                        currentContract={reduxProps.currentContract}
                        type="SERVICE_CONTRACT"
                    />
                }
                {
                    reduxProps.currentContractDebit && selectedAction === "CREATE_PAYMENT" &&
                    <CreatePayment
                        open={!!reduxProps.currentContractDebit}
                        onCancel={() => { closeModal() }}
                        onAccept={() => {
                            closeModal()
                            initData()
                            openNotification(
                                createDescriptions.SUCCESS_MODAL.TITLE,
                                createDescriptions.SUCCESS_MODAL.DESCRIPTION,
                            )
                        }}
                        contractDebit={reduxProps.currentContractDebit}
                    />
                }
                {renderLoader()}
            </PageCard>
            {
                selectedAction && refId &&
                <AuthTokenValidator
                    open={tokenValidatorOpen}
                    onCancel={() => {
                        setTokenValidatorOpen(false)
                    }}
                    onAccept={onValidateAuthTokenSuccess}
                    purpose={selectedAction}
                    refId={refId}
                />
            }
            {
                serviceContractToDelete &&
                <ContractDebitDelete
                    open={true}
                    onCancel={() => { setServiceContractToDelete(undefined) }}
                    onAccept={() => {
                        setServiceContractToDelete(undefined)
                        initData()
                        openNotification(
                            deleteDescriptions.SUCCESS_MODAL.TITLE,
                            deleteDescriptions.SUCCESS_MODAL.DESCRIPTION,
                        )
                    }}
                    type={"SERVICE_CONTRACT"}
                    contractDebit={serviceContractToDelete}
                />
            }
        </>
    )
}
